import type { KenhBanHang, KenhBanHangInput } from '@/types/schemas';
import { ActionBar, DeleteDialog, FormDialog } from './components';
import { ErpDataTableV2 } from '@/components/custom/erp';
import { getDataTableColumns } from './cols-definition';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function DanhMucKenhBanHang() {
  const {
    data: salesChannels,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem: addKenhBanHang,
    updateItem: updateKenhBanHang,
    deleteItem: deleteKenhBanHang,
    refreshData: refreshKenhBanHangs,
    isLoading,
    searchData
  } = useCRUD<KenhBanHang, KenhBanHangInput>({
    endpoint: QUERY_KEYS.KENH_BAN_HANG,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<KenhBanHang>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : salesChannels || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = getDataTableColumns();

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: columns
    }
  ];

  const handleAddSalesChannel = async (data: KenhBanHangInput) => {
    await addKenhBanHang(data);
    await refreshKenhBanHangs();
  };

  const handleUpdateSalesChannel = async (data: KenhBanHangInput) => {
    if (selectedObj) {
      await updateKenhBanHang(selectedObj.uuid, data);
      await refreshKenhBanHangs();
    }
  };

  const handleFormSubmit = async (data: KenhBanHangInput) => {
    try {
      if (formMode === 'add') {
        await handleAddSalesChannel(data);
      } else if (formMode === 'edit' && selectedObj) {
        await handleUpdateSalesChannel(data);
      }
      handleCloseForm();
      clearSelection();
    } catch (err: unknown) {
      return;
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedObj) {
      try {
        await deleteKenhBanHang(selectedObj.uuid);
        await refreshKenhBanHangs();
        handleCloseDelete();
        clearSelection();
      } catch {
        return;
      }
    }
  };

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <ActionBar
        onAddIconClick={handleAddClick}
        onEditIconClick={handleEditClick}
        onDeleteIconClick={handleDeleteClick}
        onCopyIconClick={handleCopyClick}
        onWatchIconClick={handleViewClick}
        hasRowSelected={!!selectedObj}
      />

      <ErpDataTableV2
        tables={tables}
        onRowClick={handleRowClick}
        selectedRowId={selectedRowIndex || undefined}
        totalItems={resolvedTotalItems}
        currentPage={resolvedCurrentPage}
        onPageChange={onPageChange}
        pageSize={PAGE_SIZE}
        onSearchChange={handleSearchChange}
      />

      {showForm && (
        <FormDialog
          mode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onCopyButtonClick={handleCopyClick}
          onWatchButtonClick={handleViewClick}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          selectedObj={selectedObj}
          isCopyMode={isCopyMode}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          onConfirmDelete={handleConfirmDelete}
        />
      )}
    </div>
  );
}
